import { type CustomEventUnsubscribe } from '../../webrtc/utils/emitter';
import { getLogger } from '../../libs/logger';
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import {
  session,
  SessionEventType,
  type SessionParticipant,
  type SessionJoinParams,
  type SessionStartParams,
  SessionConnectionStatus,
} from '../../webrtc/session';
import { type AppDispatch, type RootState } from '../../stores/redux';
import { getLegacyIdForMember } from '../realtime/realtime.selectors';
import {
  getPendingParticipants,
  getRemovingParticipantTimes,
} from './session.selectors';

const logger = getLogger('SessionSlice');
let unsubscribeCallbacks: CustomEventUnsubscribe[] = [];

// used for testing only
export const _getUnsubscribeCallbacks = (): CustomEventUnsubscribe[] => [
  ...unsubscribeCallbacks,
];
export const _setUnsubscribeCallbacks = (
  callbacks: CustomEventUnsubscribe[],
): void => {
  unsubscribeCallbacks = callbacks;
};

export type SessionSlice = {
  participantsMap: Record<string, SessionParticipant>;
  allParticipantsMap: Record<string, SessionParticipant>;
  connectionStatus?: SessionConnectionStatus;
  isLoading: boolean;
  sessionId?: string;
  pendingParticipants?: Record<string, number>;
  removingParticipantTimes: Record<string, number>;
};

export const initialState: SessionSlice = {
  participantsMap: session.participantsMap,
  allParticipantsMap: session.participantsMap,
  connectionStatus: undefined,
  isLoading: false,
  sessionId: undefined,
  pendingParticipants: {},
  removingParticipantTimes: {},
};

export const connect = async (
  method: () => Promise<unknown>,
): Promise<void> => {
  if (session.connectionStatus === SessionConnectionStatus.Connected) {
    return;
  }

  try {
    await method();
  } catch (connectError) {
    logger.error('[session/connect]', connectError);
    throw connectError;
  }
};

export const end = createAsyncThunk('session/end', async () => {
  unsubscribe();
  return session.end();
});

export const start = createAsyncThunk(
  'session/start',
  async (payload: SessionStartParams) => {
    await connect(async () => session.start(payload));
    return session.sessionId;
  },
);

export const disconnect = createAsyncThunk('session/disconnect', async () => {
  try {
    await session.disconnect();
  } catch (disconnectError) {
    logger.error('[session/disconnect]', disconnectError);
    throw disconnectError;
  }
});

export const reconnect = createAsyncThunk('session/reconnect', async () => {
  try {
    await session.reconnect();
  } catch (reconnectError) {
    logger.error('[session/reconnect]', reconnectError);
    throw reconnectError;
  }
});

export const join = createAsyncThunk(
  'session/join',
  async (payload: SessionJoinParams) => {
    return connect(async () => {
      await session.join(payload);
      await session.connect();
    });
  },
);

export const initialize = createAsyncThunk<
  Promise<void>,
  undefined,
  { state: RootState; dispatch: AppDispatch }
>('session/initialize', async (_, { dispatch, getState }) => {
  if (unsubscribeCallbacks.length > 0) {
    return;
  }
  unsubscribeCallbacks = [
    session.on([SessionEventType.ParticipantJoined], ({ data }) => {
      dispatch(
        sessionSlice.actions._update({
          participantsMap: session.participantsMap,
        }),
      );
      dispatch(
        sessionSlice.actions._update({
          allParticipantsMap: session.participantsMap,
        }),
      );

      if (data?.participantId !== undefined) {
        const pendingStartTime =
          getPendingParticipants(getState())[data.participantId];
        removeParticipantFromPending(data.participantId, dispatch, getState);
        const joiningDuration =
          pendingStartTime !== undefined
            ? Date.now() - pendingStartTime
            : undefined;

        logger.info('[SessionEventType.ParticipantJoined] Participant joined', {
          participantId: data.participantId,
          joiningDuration,
        });
      }
    }),

    session.on([SessionEventType.SessionDisconnected], () => {
      logger.debug(`[session/${SessionEventType.SessionDisconnected}] Debug:`);
      dispatch(
        sessionSlice.actions._update({
          participantsMap: session.participantsMap,
          pendingParticipants: {},
        }),
      );
    }),

    session.on(
      [SessionEventType.ParticipantUpdated, SessionEventType.ParticipantLeft],
      (event) => {
        if (
          event.type === SessionEventType.ParticipantLeft &&
          event.data?.participantId !== undefined
        ) {
          const removingStartTime =
            getRemovingParticipantTimes(getState())[event.data.participantId];
          const removingDuration =
            removingStartTime !== undefined
              ? Date.now() - removingStartTime
              : undefined;
          dispatch(removeRemovingParticipant(event.data.participantId));
          logger.info('[SessionEventType.ParticipantLeft] Participant left', {
            participantId: event.data.participantId,
            removingDuration,
          });
        }
        dispatch(
          sessionSlice.actions._update({
            participantsMap: session.participantsMap,
          }),
        );
      },
    ),

    session.on([SessionEventType.ConnectionStatusChanged], (event) => {
      dispatch(
        sessionSlice.actions._update({
          connectionStatus: event.data?.connectionStatus,
        }),
      );

      /**
       * If session is disconnected, end the call. End call also set the connectionStatus to Disconnected
       * but it doesn't fire the SessionEventType.ConnectionStatusChanged
       */
      if (
        event.data?.connectionStatus === SessionConnectionStatus.Disconnected
      ) {
        void dispatch(end());
      }
    }),
  ];
});

export const unsubscribe = createAsyncThunk('session/unsubscribe', () => {
  for (const unsubscribeCallback of unsubscribeCallbacks) {
    unsubscribeCallback();
  }
});

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    _update: (state, action: PayloadAction<Partial<SessionSlice>>) => {
      // https://github.com/reduxjs/redux-toolkit/issues/1423
      Object.assign(state, action.payload);
    },
    setSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
    },
    addPendingParticipants(state, action: PayloadAction<string[]>) {
      const now = Date.now();
      state.pendingParticipants ??= {};
      for (const participantId of action.payload) {
        state.pendingParticipants[participantId] = now;
      }
    },
    removePendingParticipant(state, action: PayloadAction<string>) {
      delete state.pendingParticipants?.[action.payload];
    },
    addRemovingParticipantTime(state, action: PayloadAction<string[]>) {
      const now = Date.now();
      for (const participantId of action.payload) {
        state.removingParticipantTimes[participantId] = now;
      }
    },
    removeRemovingParticipant(state, action: PayloadAction<string>) {
      delete state.removingParticipantTimes[action.payload];
    },
    reset(state, _action: PayloadAction<string>) {
      state.pendingParticipants = {};
      state.participantsMap = {};
      state.connectionStatus = SessionConnectionStatus.Disconnected;
      state.allParticipantsMap = {};
      state.isLoading = false;
      state.sessionId = undefined;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(end.pending, (state, _action) => {
        return {
          ...state,
          pendingParticipants: {},
        };
      })
      .addCase(start.fulfilled, (state, _action) => {
        return {
          ...state,
          sessionId: session.sessionId,
          isLoading: false,
        };
      })
      .addCase(start.pending, (state, _action) => {
        return { ...state, isLoading: true };
      })
      .addCase(join.fulfilled, (state, _action) => {
        return {
          ...state,
          participantsMap: session.participantsMap,
          allParticipantsMap: session.participantsMap,
          isLoading: false,
        };
      })
      .addCase(join.pending, (state, _action) => {
        return { ...state, isLoading: true };
      });
  },
});

export const {
  addPendingParticipants,
  removePendingParticipant,
  addRemovingParticipantTime,
  removeRemovingParticipant,
  setSessionId,
} = sessionSlice.actions;

export function removeParticipantFromPending(
  participantId: string,
  dispatch: AppDispatch,
  getState: () => RootState,
): void {
  dispatch(removePendingParticipant(participantId));

  const participantLegacyId = getLegacyIdForMember(getState(), participantId);
  if (participantLegacyId !== undefined) {
    dispatch(removePendingParticipant(participantLegacyId));
  }
}
